import { Injectable, inject } from '@angular/core';
import { GraphPeriod } from '@cropx-common/components/graphs/models/GraphsModels';
import { MoistureBarContent } from '@cropx-common/components/overview/models/FieldsModel';
import { SuspendedState } from '@cropx-common/models/SuspendedState';
import {
  DeviceApiService,
  DeviceInstallation,
  LayoutTypes,
  SensorDeviceInstallation,
} from '@cropx-common/services/api-gateway/DeviceApiService';
import { AssignmentRequest } from '@cropx-common/state/subscriptions/model/SubscriptionsResponse';
import { MissingPayingEntityDeviceSelectors } from '@cropx-common/state/subscriptions/subscriptions-missing-paying-entity.state';
import { FieldGraphSelectors } from '@cropx-web/screens/field/state/field-graph.state';
import { FieldSelectors } from '@cropx-web/screens/field/state/field.state';
import { DevicesSelectors } from '@cropx-web/state/devices.state';
import {
  Action,
  Selector,
  State,
  StateContext,
  createPropertySelectors,
} from '@ngxs/store';
import { Observable, tap } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace SensorSummaryPage {
  const type = 'SensorSummaryPage';

  export class SetActiveFieldNode {
    static readonly type = `[${type}] SetActiveFieldNode`;

    constructor(
      public fieldNodeUuid: string,
      public fetchDeviceDetails: boolean = true,
    ) {}
  }

  export class GetDeviceInstallation {
    static readonly type = `[${type}] GetDeviceInstallation`;

    constructor(public deviceInstallationUuid: string) {}
  }

  export class SetPeriod {
    static readonly type = `[${type}] SetPeriod`;

    constructor(public period: GraphPeriod) {}
  }

  export class SetFromTo {
    static readonly type = `[${type}] SetFromTo`;

    constructor(
      public from: number,
      public to: number,
    ) {}
  }
}

type SensorSummaryPageModel = {
  activeFieldNodeUuid: string;
  deviceInstallation: DeviceInstallation;
  period: GraphPeriod;
  from: number;
  to: number;
};

@State<SensorSummaryPageModel>({
  name: 'sensorSummaryPage',
  defaults: {
    activeFieldNodeUuid: null,
    deviceInstallation: null,
    period: GraphPeriod.MONTH,
    from: null,
    to: null,
  },
})
@Injectable()
export class SensorSummaryPageState {
  private api = inject(DeviceApiService);

  @Action(SensorSummaryPage.SetActiveFieldNode)
  setActiveSensor(
    ctx: StateContext<SensorSummaryPageModel>,
    action: SensorSummaryPage.SetActiveFieldNode,
  ): void {
    ctx.patchState({
      activeFieldNodeUuid: action.fieldNodeUuid,
    });
    if (action.fetchDeviceDetails) {
      ctx.dispatch(
        new SensorSummaryPage.GetDeviceInstallation(action.fieldNodeUuid),
      );
    }
  }

  @Action(SensorSummaryPage.SetPeriod)
  setPeriod(
    ctx: StateContext<SensorSummaryPageModel>,
    action: SensorSummaryPage.SetPeriod,
  ): void {
    ctx.patchState({
      period: action.period,
    });
  }

  @Action(SensorSummaryPage.SetFromTo)
  setFromTo(
    ctx: StateContext<SensorSummaryPageModel>,
    action: SensorSummaryPage.SetFromTo,
  ): void {
    ctx.patchState({
      from: action.from,
      to: action.to,
    });
  }

  @Action(SensorSummaryPage.GetDeviceInstallation)
  getDeviceInstallation(
    ctx: StateContext<SensorSummaryPageModel>,
    action: SensorSummaryPage.GetDeviceInstallation,
  ): Observable<any> {
    return this.api.getDeviceInstallation(action.deviceInstallationUuid).pipe(
      tap((deviceInstallation) => {
        ctx.patchState({
          deviceInstallation: deviceInstallation,
        });
      }),
    );
  }
}

export class SensorSummaryPageSelectors {
  static slices = createPropertySelectors<SensorSummaryPageModel>(
    SensorSummaryPageState,
  );

  @Selector([
    FieldSelectors.sensors,
    SensorSummaryPageSelectors.slices.activeFieldNodeUuid,
  ])
  static selectedSensor(
    sensors: SensorDeviceInstallation[],
    fieldNodeUuid: string,
  ): SensorDeviceInstallation {
    if (!sensors) {
      return null;
    }

    const sensor = sensors.find((ss) => ss.uuid === fieldNodeUuid);
    return sensor;
  }

  @Selector([
    DevicesSelectors.slices.devicesInstallations,
    SensorSummaryPageSelectors.slices.activeFieldNodeUuid,
  ])
  static selectedDevice(
    sensors: SensorDeviceInstallation[],
    fieldNodeUuid: string,
  ): SensorDeviceInstallation {
    if (!sensors) {
      return null;
    }

    const sensor = sensors.find((ss) => ss.uuid === fieldNodeUuid);
    return sensor;
  }

  @Selector([FieldSelectors.sensors])
  static activeSensors(
    sensors: SensorDeviceInstallation[],
  ): SensorDeviceInstallation[] {
    if (!sensors) {
      return null;
    }

    return sensors.filter((ss) => ss.deactivationTimestamp === null);
  }

  @Selector([SensorSummaryPageSelectors.activeSensors])
  static activeFieldSensors(
    sensors: SensorDeviceInstallation[],
  ): SensorDeviceInstallation[] {
    if (!sensors) {
      return null;
    }

    return sensors.filter(
      (ss) => ![LayoutTypes.WeatherStation].includes(ss.sensorType),
    );
  }

  @Selector([
    SensorSummaryPageSelectors.selectedSensor,
    FieldGraphSelectors.moistureBarDataSelector,
  ])
  static moistureBarData(
    sensor: SensorDeviceInstallation,
    selector: (fieldNodeUuid: string) => MoistureBarContent,
  ): MoistureBarContent {
    if (!sensor) {
      return null;
    }

    const moistureBar = selector(sensor.uuid);
    return moistureBar;
  }

  @Selector([
    SensorSummaryPageSelectors.selectedSensor,
    FieldGraphSelectors.moistureBarStateSelector,
  ])
  static moistureBarDataState(
    sensor: SensorDeviceInstallation,
    selector: (fieldNodeUuid: string) => SuspendedState,
  ): SuspendedState {
    if (!sensor) {
      return null;
    }
    const moistureBar = selector(sensor.uuid);
    return moistureBar;
  }

  @Selector([
    SensorSummaryPageSelectors.selectedSensor,
    FieldGraphSelectors.sensorLoadingSelector,
  ])
  static moistureBarLoading(
    sensor: SensorDeviceInstallation,
    selector: ReturnType<typeof FieldGraphSelectors.sensorLoadingSelector>,
  ): boolean {
    if (!sensor) {
      return null;
    }

    return selector(sensor.uuid, 'moisturebar');
  }

  @Selector([
    SensorSummaryPageSelectors.selectedSensor,
    FieldGraphSelectors.sensorSuspendedSelector,
  ])
  static moistureBarSuspended(
    sensor: SensorDeviceInstallation,
    selector: ReturnType<typeof FieldGraphSelectors.sensorSuspendedSelector>,
  ): SuspendedState {
    if (!sensor) {
      return null;
    }

    return selector(sensor.uuid, 'moisturebar');
  }

  @Selector([
    SensorSummaryPageSelectors.selectedSensor,
    MissingPayingEntityDeviceSelectors.missingPayingEntityBySerialSelector,
  ])
  static isMissingSpe(
    sensor: SensorDeviceInstallation,
    selector: (serialNumber: string) => boolean,
  ): boolean {
    if (!sensor) {
      return null;
    }
    return selector(sensor.serialNumber);
  }

  @Selector([
    SensorSummaryPageSelectors.selectedSensor,
    MissingPayingEntityDeviceSelectors.assignmentRequestSelector,
  ])
  static assignmentRequest(
    sensor: SensorDeviceInstallation,
    selector: (serialNumber: string) => AssignmentRequest,
  ): AssignmentRequest {
    if (!sensor) {
      return null;
    }
    return selector(sensor.serialNumber);
  }

  @Selector([SensorSummaryPageSelectors.selectedSensor])
  static hasMoistureBar(sensor: SensorDeviceInstallation): boolean {
    if (!sensor) {
      return null;
    }
    const hasMoisture = [
      LayoutTypes.CropX,
      LayoutTypes.Apex,
      LayoutTypes.EnviroPro,
      LayoutTypes.EnviroProEc,
      LayoutTypes.Sentek,
      LayoutTypes.AquaCheck,
      LayoutTypes.Terrasen,
      LayoutTypes.Sensorless,
    ].includes(sensor.sensorType);

    return hasMoisture;
  }
}
